/**
 * @file Contributor UI — PublicationDataFieldGroups.
 */

import React, { JSX } from "react";
import { FormField } from "../../../../../../interfaces/formInterfaces";

/** Next field is the companion unit for publication functional unit (CMS may name it differently). */
function isFunctionalUnitUnitCompanion(
  functionalUnitField: FormField,
  candidate: FormField | undefined
): candidate is FormField {
  if (!functionalUnitField.isEnabled || functionalUnitField.name !== "functionalUnit") {
    return false;
  }
  if (!candidate?.isEnabled) return false;
  if (candidate.type === "UNIT") {
    const n = candidate.name.toLowerCase();
    return (
      n === "unit" ||
      n.includes("functionalunit") ||
      n.includes("functional_unit")
    );
  }
  const n = candidate.name.toLowerCase();
  return (
    n === "functionalunitunit" ||
    n === "functionalunitunitid" ||
    n === "functional_unit_unit" ||
    n === "functional_unit_unit_id" ||
    (n.includes("functionalunit") && n.includes("unit") && n !== "functionalunit")
  );
}

interface PublicationDataFieldGroupsProps {
  allFields: FormField[];
  renderField: (field: FormField) => JSX.Element;
}

const PublicationDataFieldGroups: React.FC<PublicationDataFieldGroupsProps> = ({
  allFields,
  renderField,
}) => {
  const renderedFields: JSX.Element[] = [];

  for (let i = 0; i < allFields.length; i++) {
    const field = allFields[i];
    if (!field.isEnabled) continue;

    const label = field.label.toLowerCase();

    // Detect grouped date fields dynamically without breaking order
    if (label.includes("publication year")) {
      const monthField = allFields[i + 1];
      const dayField = allFields[i + 2];
      if (
        monthField?.label.toLowerCase().includes("publication month") &&
        dayField?.label.toLowerCase().includes("publication day")
      ) {
        renderedFields.push(
          <div key={`pub-date-${field.id}`} className="form-row date-group">
            {renderField(field)}
            {renderField(monthField)}
            {renderField(dayField)}
          </div>
        );
        i += 2;
        continue;
      }
    }

    if (label.includes("epd publication year")) {
      const monthField = allFields[i + 1];
      const dayField = allFields[i + 2];
      if (
        monthField?.label.toLowerCase().includes("epd publication month") &&
        dayField?.label.toLowerCase().includes("epd publication day")
      ) {
        renderedFields.push(
          <div key={`epd-date-${field.id}`} className="form-row date-group">
            {renderField(field)}
            {renderField(monthField)}
            {renderField(dayField)}
          </div>
        );
        i += 2;
        continue;
      }
    }

    if (label.includes("validity year")) {
      const monthField = allFields[i + 1];
      const dayField = allFields[i + 2];
      if (
        monthField?.label.toLowerCase().includes("validity month") &&
        dayField?.label.toLowerCase().includes("validity day")
      ) {
        renderedFields.push(
          <div
            key={`validity-date-${field.id}`}
            className="form-row date-group"
          >
            {renderField(field)}
            {renderField(monthField)}
            {renderField(dayField)}
          </div>
        );
        i += 2;
        continue;
      }
    }

    // Production plant fields in 2-2 grid
    if (field.name === "productionPlantLatitude") {
      // Find all production plant fields
      const latitudeField = field;
      const longitudeField = allFields.find(
        (f) => f.name === "productionPlantLongitude"
      );
      const stateField = allFields.find(
        (f) => f.name === "productionPlantState"
      );
      const districtField = allFields.find(
        (f) => f.name === "productionPlantDistrict"
      );
      const addressField = allFields.find(
        (f) => f.name === "productionPlantAddress"
      );
      const pincodeField = allFields.find(
        (f) => f.name === "productionPlantPincode"
      );

      if (
        longitudeField &&
        stateField &&
        districtField &&
        addressField &&
        pincodeField
      ) {
        renderedFields.push(
          <div key="production-plant-grid" className="production-plant-grid">
            <div className="grid-row">
              {renderField(latitudeField)}
              {renderField(longitudeField)}
            </div>
            <div className="grid-row">
              {renderField(stateField)}
              {renderField(districtField)}
            </div>
            <div className="grid-row">
              {renderField(addressField)}
              {renderField(pincodeField)}
            </div>
          </div>
        );
        continue;
      }
    }

    // Skip if already rendered in production plant grid
    if (
      [
        "productionPlantLongitude",
        "productionPlantState",
        "productionPlantDistrict",
        "productionPlantAddress",
        "productionPlantPincode",
      ].includes(field.name)
    ) {
      continue;
    }

    // Functional unit + unit on one row (matches LCA-style value + unit layout)
    if (isFunctionalUnitUnitCompanion(field, allFields[i + 1])) {
      const unitField = allFields[i + 1];
      renderedFields.push(
        <div key={`functional-unit-${field.id}`} className="form-row functional-unit-row">
          {renderField(field)}
          {renderField(unitField)}
        </div>
      );
      i += 1;
      continue;
    }

    // Default (non-grouped) field
    renderedFields.push(renderField(field));
  }

  return <>{renderedFields}</>;
};

/** React component — Publication Data Field Groups. */
export default PublicationDataFieldGroups;

